/**
 * Preisspiegel product catalog (#2388) — the app's entry point to the catalog.
 *
 * **This module holds no catalog data.** The Warengruppe → Kategorie → item
 * tree, the five axis vocabularies and their German labels all live in
 * ``config/catalog/preisspiegel@v1.yaml`` and are compiled into
 * ``./catalog.generated`` by ``frontend/scripts/generate-preisspiegel-catalog.mjs``
 * (#2771). What is left here is the hand-written part that codegen has no
 * business producing: the lookup helpers and the structural validator.
 *
 * Until #2771 this file was a 471-line hand-authored MIRROR of that YAML, kept
 * honest by an indentation-oriented drift guard. Adding one tile then meant a
 * four-file, three-language edit; it is now an edit to the YAML plus
 * ``npm run codegen``, and touches nothing in ``frontend/src``.
 *
 * Consumers import from **this** module, not from ``./catalog.generated`` — the
 * re-export below keeps every existing import path working and leaves one place
 * to add a helper.
 *
 * #2538 — ALSO consumed outside this app: the Deckungsbeitrag drawer imports
 * ``findItem`` to derive the ``?kat=`` of its Preisspiegel deep link (the catalog
 * is the only authority on which Kategorie an item lives under). Keep this module
 * import-free so that edge can never pull React or the data-plane across an app
 * boundary, and never introduce a module cycle.
 */
import {
  CATALOG,
  CHANNELS,
  PRICE_BASES,
  PRICE_SIDES,
  PRICE_UNITS,
  PRODUCT_CATEGORIES,
  QUALITIES,
  type Kategorie,
  type TrackedItem,
  type Warengruppe,
} from './catalog.generated';

export * from './catalog.generated';

/** A tracked item plus its position in the tree (for lookups / drill-ups). */
export interface ItemLocation {
  item: TrackedItem;
  kategorie: Kategorie;
  warengruppe: Warengruppe;
}

/**
 * A DERIVED leaf tile (HOF-299 S3, 2026-09-04) — one of the tenant's own `product`
 * entities, served by `GET /twin/preisspiegel/tiles` as `derived_tiles.items[]`.
 *
 * Tiles for exact products, Sorten and Arbeitsgänge are never hardcoded in the
 * catalog YAML: the static `CATALOG` holds structure (Warengruppen, Kategorien) and
 * per derived Kategorie one static fallback / aggregate tile; the leaves come from
 * the tenant's data. A served tile IS a `TrackedItem` — same shape, same consumers
 * (the grid, the chart, `findItem`) — plus the Kategorie it belongs to and a marker.
 * `id` is the `product` entity id, which is also the `catalog_item_id` its price
 * facts carry.
 */
export interface ServedTile extends TrackedItem {
  /** The derived Kategorie (L2) the product's type rule assigned it to. */
  kategorie: string;
  derived: true;
}

/**
 * A derived tile as `/tiles` sends it — the wire shape, before the axis values
 * are checked. Structural on purpose: this module imports nothing (see the header),
 * so it cannot name the service's type; the service's `PreisspiegelDerivedTile`
 * satisfies it.
 */
export interface ServedTileWire {
  id: string;
  name: string;
  unit: string;
  sides: readonly string[];
  kategorie: string;
  basis?: string;
}

/**
 * The served tiles the page can actually use (HOF-299 S3).
 *
 * The wire carries plain strings; a `TrackedItem` carries the catalog's union types,
 * and the ~15 consumers behind them were written against those types (the whole
 * reason the catalog is codegen and not a runtime fetch — see the generator). So a
 * tile is admitted only when its `unit`, every `side` and its `basis` (if any) are
 * values the compiled vocabularies know. Anything else is DROPPED here rather than
 * rendered as a blank cell somewhere downstream; the server counts what it could not
 * serve, and a unit the catalog does not know is a config change, not a page bug.
 */
export function servedTilesFromWire(rows: readonly ServedTileWire[]): ServedTile[] {
  const out: ServedTile[] = [];
  for (const row of rows) {
    if (!row || typeof row.id !== 'string' || !row.id || typeof row.kategorie !== 'string') continue;
    if (!(PRICE_UNITS as readonly string[]).includes(row.unit)) continue;
    const sides = Array.isArray(row.sides) ? row.sides : [];
    if (sides.length === 0 || !sides.every((s) => (PRICE_SIDES as readonly string[]).includes(s))) continue;
    if (row.basis !== undefined && !(PRICE_BASES as readonly string[]).includes(row.basis)) continue;
    out.push({
      id: row.id,
      name: typeof row.name === 'string' && row.name ? row.name : row.id,
      unit: row.unit as TrackedItem['unit'],
      sides: [...sides] as TrackedItem['sides'],
      ...(row.basis !== undefined ? { basis: row.basis as NonNullable<TrackedItem['basis']> } : {}),
      kategorie: row.kategorie,
      derived: true,
    });
  }
  return out;
}

/** Every tracked item across the catalog, in tree order. */
export function allItems(catalog: Warengruppe[] = CATALOG): TrackedItem[] {
  return catalog.flatMap((wg) => wg.kategorien.flatMap((k) => k.items));
}

/**
 * Locate a tracked item (+ its parents) by id, or null.
 *
 * SERVED FIRST (HOF-299 S3): a tile in `served` — the tenant's derived leaves off the
 * last `/tiles` response — wins over the static catalog, and is located under the
 * Kategorie it names. Only then the static tree. Both consult the SAME `catalog` for
 * the parents, so a served tile whose Kategorie the catalog does not know (or knows
 * as static) resolves to nothing rather than to a guessed lens — a product can only
 * live under a `leaves: derived` Kategorie.
 */
export function findItem(
  id: string,
  catalog: Warengruppe[] = CATALOG,
  served: readonly ServedTile[] = [],
): ItemLocation | null {
  const tile = served.find((t) => t.id === id);
  if (tile) {
    for (const warengruppe of catalog) {
      const kategorie = warengruppe.kategorien.find((k) => k.id === tile.kategorie);
      if (kategorie?.leaves === 'derived') return { item: tile, kategorie, warengruppe };
    }
    return null;
  }
  for (const warengruppe of catalog) {
    for (const kategorie of warengruppe.kategorien) {
      const item = kategorie.items.find((i) => i.id === id);
      if (item) return { item, kategorie, warengruppe };
    }
  }
  return null;
}

/** Locate a Kategorie by id (searching every Warengruppe), or null. */
export function findKategorie(id: string, catalog: Warengruppe[] = CATALOG): Kategorie | null {
  for (const wg of catalog) {
    const k = wg.kategorien.find((kat) => kat.id === id);
    if (k) return k;
  }
  return null;
}

/** Locate a Warengruppe by id, or null. */
export function findWarengruppe(id: string, catalog: Warengruppe[] = CATALOG): Warengruppe | null {
  return catalog.find((wg) => wg.id === id) ?? null;
}

/**
 * What a `?kat=` value names, if anything the catalog knows (HOF-299 S3).
 *
 * `saatgut`, `duengemittel`, `pflanzenschutz` and `maschinen_arbeit` were Kategorie
 * ids until 2026-09-04 and are Warengruppe ids now (`pflanzgut` made the same move
 * a day earlier). They sit in bookmarks, in the two e2e specs and in every stored
 * `hofsync.preisspiegel.kat` preference, so a link naming one keeps working: it
 * resolves to that Warengruppe's first data-bearing Kategorie — the page and
 * `/tiles` apply the same rule (`resolveSelectedKategorie`, `_resolve_scope`). An
 * id that is neither is `null`, which lets the next candidate in the page's
 * precedence stand exactly as an unknown id always has.
 */
export function resolveKategorieScope(
  id: string | null | undefined,
  catalog: Warengruppe[] = CATALOG,
): { kind: 'kategorie' | 'warengruppe'; id: string } | null {
  if (!id) return null;
  if (findKategorie(id, catalog)) return { kind: 'kategorie', id };
  if (findWarengruppe(id, catalog)) return { kind: 'warengruppe', id };
  return null;
}

/**
 * The tenant's EFFECTIVE catalog: the static tree with each derived Kategorie's
 * served tiles merged in ahead of its static aggregates (HOF-299 S3).
 *
 * The served tiles come first, in the order the server gave them (display name,
 * German collation), and the static items — the fallback / aggregate tiles that
 * keep the legacy ids — last, the way a Kultur's „Sorte unbekannt" tile closes its
 * grid. A tile naming a Kategorie the catalog does not know, or knows as static,
 * is dropped: a product may only ever appear under a `leaves: derived` Kategorie,
 * and the server-side type rule is the only thing that assigns one. Pure; a new
 * tree, the static one untouched — `CATALOG` stays the compiled artefact.
 */
export function mergeDerivedItems(catalog: Warengruppe[], served: readonly ServedTile[]): Warengruppe[] {
  if (served.length === 0) return catalog;
  const byKategorie = new Map<string, ServedTile[]>();
  for (const tile of served) {
    const bucket = byKategorie.get(tile.kategorie);
    if (bucket) bucket.push(tile);
    else byKategorie.set(tile.kategorie, [tile]);
  }
  return catalog.map((wg) => ({
    ...wg,
    kategorien: wg.kategorien.map((kat) => {
      const tiles = kat.leaves === 'derived' ? byKategorie.get(kat.id) : undefined;
      return tiles?.length ? { ...kat, items: [...tiles, ...kat.items] } : kat;
    }),
  }));
}

/**
 * Structural / schema validation of a catalog. Returns a list of human-readable
 * problems (empty ⇒ valid). Pure — safe to call at load time.
 *
 * The generator applies the same rules to the YAML at build time and refuses to
 * emit a catalog that breaks them, so on the shipped ``CATALOG`` this can only
 * fail if someone hand-edited the generated artefact — which is exactly the case
 * worth catching, since a hand edit survives until the next regeneration. It is
 * also the validator callers get for a catalog they built themselves (tests
 * pass hand-made trees through it).
 */
export function validateCatalog(catalog: Warengruppe[] = CATALOG): string[] {
  const problems: string[] = [];
  const seenItemIds = new Set<string>();
  const seenWgIds = new Set<string>();
  /**
   * Kategorie ids are checked catalog-WIDE, not per Warengruppe — matching the
   * generator's rule exactly. `findKategorie` returns the first match across
   * every Warengruppe and #2538's Deckungsbeitrag `?kat=` deep link is built from
   * it, so a duplicate in a second Warengruppe is a silently wrong link. Scoping
   * this per-Warengruppe (as it was before #2771) left the validator unable to
   * catch the one case its own docstring says it exists for.
   */
  const seenKategorieIds = new Map<string, string>();
  /**
   * Pflanzgut nach Sorten (2026-09) — the Kultur/Sorte contract, mirrored from the
   * generator's `registerKulturItems`. A producer asks the catalog "the tile for
   * this (crop, variety)" and, failing that, "the fallback tile for this crop", and
   * both must have exactly one answer: one crop per Kategorie, one Kategorie per
   * crop, exactly one item per crop without `variety`, `(crop, variety)` unique.
   */
  const crops = new Map<string, { kategorie: string; fallback: string | null; varieties: Map<string, string> }>();
  /**
   * HOF-299 S3 — derived leaves, mirrored from the generator: the four derived fields
   * come together or not at all, `fallbackItem` is one of the Kategorie's own items,
   * `productCategory` is in the closed set, and the derived Kategorien of one product
   * category share one type rule (it decides AMONG them).
   */
  const typeRuleByCategory = new Map<string, string>();
  const slug = /^[a-z0-9_]+$/;

  if (catalog.length === 0) problems.push('catalog is empty');

  for (const wg of catalog) {
    if (!slug.test(wg.id)) problems.push(`warengruppe id not a slug: "${wg.id}"`);
    if (seenWgIds.has(wg.id)) problems.push(`duplicate warengruppe id: "${wg.id}"`);
    seenWgIds.add(wg.id);
    if (!wg.name) problems.push(`warengruppe "${wg.id}" has no name`);
    if (wg.kategorien.length === 0) problems.push(`warengruppe "${wg.id}" has no kategorien`);

    const seenKatIds = new Set<string>();
    for (const kat of wg.kategorien) {
      if (!slug.test(kat.id)) problems.push(`kategorie id not a slug: "${kat.id}"`);
      if (seenKatIds.has(kat.id)) problems.push(`duplicate kategorie id in "${wg.id}": "${kat.id}"`);
      seenKatIds.add(kat.id);
      const firstSeenIn = seenKategorieIds.get(kat.id);
      if (firstSeenIn !== undefined && firstSeenIn !== wg.id) {
        problems.push(`kategorie id "${kat.id}" appears in both "${firstSeenIn}" and "${wg.id}"`);
      }
      if (firstSeenIn === undefined) seenKategorieIds.set(kat.id, wg.id);
      if (!kat.name) problems.push(`kategorie "${kat.id}" has no name`);
      if (kat.items.length === 0) problems.push(`kategorie "${kat.id}" has no items`);

      if (kat.leaves !== undefined && kat.leaves !== 'derived') {
        problems.push(`kategorie "${kat.id}" has bad leaves: "${String(kat.leaves)}"`);
      }
      if (kat.leaves === 'derived') {
        if (kat.derivedFrom !== 'product') {
          problems.push(`kategorie "${kat.id}" is derived from "${String(kat.derivedFrom)}", not product`);
        }
        if (!kat.productCategory || !PRODUCT_CATEGORIES.includes(kat.productCategory)) {
          problems.push(`kategorie "${kat.id}" has bad product_category: "${String(kat.productCategory)}"`);
        } else {
          const decidedBy = typeRuleByCategory.get(kat.productCategory);
          if (decidedBy !== undefined && kat.typeRule && decidedBy !== kat.typeRule) {
            problems.push(
              `product_category "${kat.productCategory}" is decided by both "${decidedBy}" and "${kat.typeRule}"`,
            );
          } else if (kat.typeRule) {
            typeRuleByCategory.set(kat.productCategory, kat.typeRule);
          }
        }
        if (!kat.typeRule || !slug.test(kat.typeRule)) problems.push(`kategorie "${kat.id}" has no type_rule`);
        if (!kat.fallbackItem || !kat.items.some((i) => i.id === kat.fallbackItem)) {
          problems.push(`kategorie "${kat.id}" fallback_item "${String(kat.fallbackItem)}" is not one of its own items`);
        }
      } else {
        for (const field of ['derivedFrom', 'productCategory', 'typeRule', 'fallbackItem'] as const) {
          if (kat[field] !== undefined) problems.push(`kategorie "${kat.id}" is static but carries ${field}`);
        }
      }

      let katCrop: string | null = null;
      for (const item of kat.items) {
        if (!slug.test(item.id)) problems.push(`item id not a slug: "${item.id}"`);
        if (item.id.includes('__')) problems.push(`item id "${item.id}" contains the series separator "__"`);
        if (seenItemIds.has(item.id)) problems.push(`duplicate item id: "${item.id}"`);
        seenItemIds.add(item.id);
        if (!item.name) problems.push(`item "${item.id}" has no name`);
        if (!PRICE_UNITS.includes(item.unit)) problems.push(`item "${item.id}" has bad unit: "${item.unit}"`);
        if (!item.sides || item.sides.length === 0) problems.push(`item "${item.id}" has no sides`);
        for (const s of item.sides ?? []) {
          if (!PRICE_SIDES.includes(s)) problems.push(`item "${item.id}" has bad side: "${s}"`);
        }
        if (new Set(item.sides).size !== item.sides.length) problems.push(`item "${item.id}" has duplicate sides`);
        for (const q of item.quality ?? []) {
          if (!QUALITIES.includes(q)) problems.push(`item "${item.id}" has bad quality: "${q}"`);
        }
        if (item.quality && item.quality.length === 0) problems.push(`item "${item.id}" declares an empty quality axis`);
        for (const c of item.channel ?? []) {
          if (!CHANNELS.includes(c)) problems.push(`item "${item.id}" has bad channel: "${c}"`);
        }
        if (item.channel && item.channel.length === 0) problems.push(`item "${item.id}" declares an empty channel axis`);
        if (item.basis !== undefined && !PRICE_BASES.includes(item.basis)) {
          problems.push(`item "${item.id}" has bad basis: "${item.basis}"`);
        }
        if (item.variety !== undefined && item.crop === undefined) {
          problems.push(`item "${item.id}" has variety without crop`);
        }
        if (item.crop !== undefined) {
          if (katCrop !== null && katCrop !== item.crop) {
            problems.push(`kategorie "${kat.id}" mixes crops "${katCrop}" and "${item.crop}"`);
          }
          katCrop = item.crop;
          let entry = crops.get(item.crop);
          if (entry === undefined) {
            entry = { kategorie: kat.id, fallback: null, varieties: new Map() };
            crops.set(item.crop, entry);
          } else if (entry.kategorie !== kat.id) {
            problems.push(`crop "${item.crop}" appears in both "${entry.kategorie}" and "${kat.id}"`);
          }
          if (item.variety === undefined) {
            if (entry.fallback !== null) {
              problems.push(`crop "${item.crop}" has two fallback items: "${entry.fallback}" and "${item.id}"`);
            }
            entry.fallback = item.id;
          } else if (entry.varieties.has(item.variety)) {
            problems.push(`crop "${item.crop}" repeats variety "${item.variety}"`);
          } else {
            entry.varieties.set(item.variety, item.id);
          }
        }
      }
    }
  }
  for (const [crop, entry] of crops) {
    if (entry.fallback === null) problems.push(`crop "${crop}" has no fallback item`);
  }
  return problems;
}
